// Core Services Barrel Export - Phase 4 Architecture
// Fundamental business logic services

// ============================================================================
// PHASE 4 MASTER SERVICE REGISTRATION
// ============================================================================
// Export master registration functions
export {
  registerAllServices,
  initializeAllServices,
  disposeAllServices,
  getServiceHealth,
  getRegisteredServices,
} from "./register";

// Export individual domain registrations for granular control
export {
  registerNotificationServices,
  initializeNotificationServices,
  disposeNotificationServices,
} from "./notifications/register";

export {
  registerCrmServices,
  initializeCrmServices,
  disposeCrmServices,
} from "./crm/register";

export {
  registerTaskServices,
  initializeTaskServices,
  disposeTaskServices,
} from "./task/register";

export {
  registerGamificationServices,
  initializeGamificationServices,
  disposeGamificationServices,
} from "./gamification/register";

export {
  registerDocumentGenerationServices,
  initializeDocumentGenerationServices,
  disposeDocumentGenerationServices,
} from "./document-generation/register";

// ============================================================================
// PHASE 4 COMPLIANT SERVICES (22 services - 100% COMPLETE)
// ============================================================================

// CRM Services (1 service - 100% Phase 4)
export * from "./client.service";
export { opportunityService, OpportunityService } from "./crm/opportunity.service";
export type { IOpportunityService } from "./crm/opportunity.service";
export { accountingService, AccountingService } from "./accounting.service";
export { ledgerService, LedgerService } from "./ledger.service";
export type { ILedgerService } from "./ledger.service";

// Document Generation Services (2 services - 100% Phase 4)
export { proposalService, ProposalService } from "./document-generation/proposal.service";
export type { IProposalService } from "./document-generation/proposal.service";
export { invoiceService, InvoiceService } from "./document-generation/invoice.service";
export type { IInvoiceService } from "./document-generation/invoice.service";

// Task Services (3 services - 100% Phase 4)
export * from "./task-activity.service";
export * from "./task-comment.service";
export * from "./task-metadata.service";

// Activity Analytics & Search Services (Phase 2 - 100% Phase 4)
export * from "./activity-analytics.service";
export * from "./activity-search.service";

// Activity Automation Services (Phase 3 - 100% Phase 4)
export * from "./activity-webhook.service";
export * from "./dsar.service";

// Integration Activity Services (Phase 3 - Google Workspace)
export * from "./integration-activity.service";

// Notification Services (7 services - 100% Phase 4)
export * from "./notifications";

// Gamification Services (4 services - 100% Phase 4)
export * from "./profile.service";
export * from "./category.service";
export * from "./seasons.service";
export * from "./achievement.service";

// Product Tier Service - 3-tier product model
export { ProductTierService, productTierService } from "./product-tier/product-tier.service";

// Cortex Voice Conversation Services
export * from "./cortex";

// ============================================================================
// MYCELIUM PROTOCOL SERVICES (NEW - 2026-02-19)
// ============================================================================
// Mycelium Event Backbone - Central nervous system for service interconnection
export { myceliumEventBackbone } from "./mycelium-event-backbone.service";
export type {
  MyceliumEvent,
  EventDomain,
  EventSubscriber,
  EventEnricher,
  EventBackboneStats,
  DeadLetterEntry,
} from "./mycelium-event-backbone.service";

// Mycelium Connector Framework - formal interface + registry
export type { IMyceliumConnector, ConnectorMetadata } from "./mycelium-connector.interface";
export { ConnectorRegistry, connectorRegistry } from "./mycelium-connector-registry";
export type { ConnectorRegistryStats } from "./mycelium-connector-registry";
// Re-export Zod schemas and validation from mycelium-core for consumers
// that need runtime validation (e.g., workers, edge functions).
export {
  EventDomainSchema,
  EventMetadataSchema,
  BackboneEventSchema,
  DeadLetterEntrySchema,
} from "@flowstate/mycelium-core";
export type { EventMetadata, BackboneEvent } from "@flowstate/mycelium-core";

// Mycelium Connectors - Exploit latent potential
export { activityGamificationConnector } from "./mycelium-activity-gamification.connector";
export { gmailCalendarTasksConnector } from "./mycelium-gmail-calendar-tasks.connector";
export { xeroCommissionGamificationConnector } from "./mycelium-xero-commission-gamification.connector";
export { notificationChannelEscalationConnector } from "./mycelium-notification-escalation.connector";
export { cortexContextEnhancementConnector } from "./mycelium-cortex-context.connector";
export { salesPipelineIntelligenceConnector } from "./mycelium-sales-pipeline-intelligence.connector";
export { documentTaskExtractionConnector } from "./mycelium-document-task-extraction.connector";
export { documentGenerationConnector } from "./mycelium-document-generation.connector";
export { myceliumTimeDependencyConnector } from "./mycelium-time-dependency.connector";
export { auditTrailConnector } from "./mycelium-audit-trail.connector";
export { teamGamificationConnector } from "./mycelium-team-gamification.connector";
export { webhookBridgeConnector } from "./mycelium-webhook-bridge.connector";
export { enqueueWebhookDelivery } from "./mycelium-webhook-enqueue";
export type { WebhookDeliveryEvent } from "./mycelium-webhook-enqueue";
export { cortexTriggerConnector } from "./mycelium-cortex-trigger.connector";

// Mycelium Initialization
export { initializeMyceliumProtocol, disposeMyceliumProtocol, getMyceliumHealth } from "./mycelium-init";

// Mycelium Transport - PGMQ only
export type { IMyceliumTransport, MyceliumEventPayload } from "./mycelium-transport.types";
export { getMyceliumTransport, setMyceliumTransport } from "./mycelium-transport-registry";
export type { MyceliumTransportType } from "./mycelium-transport-registry";
export { PgmqMyceliumTransport } from "./mycelium-transport-pgmq";
export type { PgmqSupabaseClient, PgmqTransportConfig } from "./mycelium-transport-pgmq";

// Mycelium PGMQ Health Monitor - event-driven health tracking
export { PgmqHealthMonitor, pgmqHealthMonitor } from "./mycelium-pgmq-health-monitor";
export type { HealthCheckEvent, HealthMonitorConfig, HealthStatus } from "./mycelium-pgmq-health-monitor";

// Mycelium Service Discovery
export { MyceliumServiceDiscovery } from "./mycelium-service-discovery";
export type {
  MyceliumServiceRecord,
  RegisterServiceParams,
  DiscoverServicesParams,
  ServiceDiscoveryClient,
  ServiceHealthStatus,
  MyceliumServiceType,
} from "./mycelium-service-discovery";

// ============================================================================
// LEGACY FUNCTION EXPORTS (backward compatibility)
// ============================================================================

// Specific function exports for components
import { gamificationService } from "./gamification.service";
export const getLeaderboard =
  gamificationService.getLeaderboard.bind(gamificationService);

// ============================================================================
// SERVICE UTILITIES
// ============================================================================
export {
  ServiceRegistry,
  serviceRegistry,
  registerService,
  getService,
  getServiceConfig,
  createServiceError,
  createServiceResponse,
  createErrorResponse,
  createSuccessResponse,
} from "./service-registry";

// Export service types
export type { 
  ServiceResponse, 
  ServiceResponseSuccess,
  ServiceResponseError,
  ServiceResponseMetadata,
  ServiceError, 
  IService, 
  ServiceConfig,
  CacheStats,
  PerformanceMetrics
} from "./service-registry";